import { Type, TLiteral, TUnion } from '@sinclair/typebox';

export const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

export const isTruthy = <T>(value: T | undefined | null | false | ''): value is T => Boolean(value);

export const isNotNullish = <T>(value: T | null | undefined): value is T => value !== null && value !== undefined;

export function isOneOf<T>(value: T, collection: Record<string, T> | { [key: string]: string }): value is T {
  return Object.values(collection).includes(value);
}

export function assertUnreachable(x: never): never {
  throw new Error(`Unhandled Code Executed: ${x}`);
}

export function assertUnreachableNoThrow(x: never): void {
  console.log(`Unhandled Code Executed: ${x}`);
}

export class InternalServerError extends Error {
  name = 'InternalServerError';

  constructor({ message }: { message: string }) {
    super(message);
  }
}

export class NotFoundError extends Error {
  name = 'NotFoundError';

  constructor({ message }: { message: string }) {
    super(message);
  }
}

export function assertNotNullish<T>(
  value: T | null | undefined,
  message: string,
  error: { new (args: { message: string }): unknown } = InternalServerError
): asserts value is T {
  if (!isNotNullish(value)) {
    throw new error({ message });
  }
}

export function assertTruthy<T>(
  value: T | undefined | null | false | '',
  message: string,
  error: { new (args: { message: string }): unknown } = InternalServerError
): asserts value is T {
  if (!isTruthy(value)) {
    throw new error({ message });
  }
}

export function assertOneOf<T>(
  value: T,
  collection: Record<string, T> | { [key: string]: string },
  message: string,
  error: { new (args: { message: string }): unknown } = InternalServerError
): asserts value is T {
  if (!isOneOf(value, collection)) {
    throw new error({ message });
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type NotAny<T> = T extends any ? (unknown extends T ? never : T) : T;

export type RequiredProps<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

export function stringToEnum<T extends Record<string, string>>(enumType: T, value: string): T[keyof T] | undefined {
  const enumValues = Object.values(enumType);
  if (enumValues.includes(value)) {
    return value as T[keyof T];
  }
  return undefined;
}

export type ValueOf<T> = T[keyof T];

export const StringEnumSchema = <T extends Record<string, string>>(
  obj: T,
  description?: string
): TUnion<{ [K in keyof T]: TLiteral<T[K]> }[keyof T][]> => {
  const literals = Object.values(obj).map(value => Type.Literal(value));
  return Type.Union(literals as { [K in keyof T]: TLiteral<T[K]> }[keyof T][], {
    description,
    examples: Object.values(obj),
  });
};
